import React from "react";
import { gameLabel, LISTING_MODE_NAMES, tradingName } from "./data.jsx";
import { useApp } from "./store.jsx";
import { api, setNameFor } from "./api.jsx";
import { hrefFor } from "./router.jsx";
import { listingLinksIn, shortListingLink } from "./listing-links.js";

const { Badge, DistanceTag, Icon, IconButton, Skeleton } = window.CardboardmarketDesignSystem_2a3239;

/*
 * A LISTING LINK IN A CONVERSATION, DRAWN AS THE LISTING.
 *
 * The owner, 2026-10-20: a link to one of our listings is shown in the chat
 * with what it is — picture, price, who has it — so nobody has to leave the
 * conversation to see what was meant. Design: .scratch/chat-listing-card/design.
 *
 * The cards come with the conversation (`openThread.cards`, keyed by listing
 * id) and are resolved by the server when it is read, so a card is the
 * listing as it is NOW: one sold since the message was sent says so. Which
 * listings a message shows is the server's answer too (`message.listings`);
 * the links in the text are found again here only to shorten them in place.
 *
 * Two sizes: 280 px with a 56×78 picture on a phone, 360 px with 72×100 in
 * the pane beside the inbox.
 */

const SIZES = {
  phone: { width: 280, gap: 12, padding: 10, picW: 56, picH: 78, stack: 3, title: 'var(--text-base)', price: 'var(--text-base)' },
  wide: { width: 360, gap: 14, padding: 12, picW: 72, picH: 100, stack: 4, title: 'var(--text-md)', price: 'var(--text-md)' },
};

const muted = { color: 'var(--text-muted)' };

/** `set · condition · language`, from whatever of the three the listing has. */
function copyLine(card) {
  return [setNameFor(card) || card.cardCode, card.grade, card.language && card.language.toUpperCase()]
    .filter(Boolean).join(' · ');
}

export function ListingLinkCard({ id, card, wide }) {
  const { go, t } = useApp();
  const size = wide ? SIZES.wide : SIZES.phone;

  // Not resolved yet: the same shape, so the conversation does not jump when it lands.
  if (!card) {
    return (
      <div role="status" aria-label={t("Loading the listing.")}>
        <Skeleton shape={wide ? 'listing-link-wide' : 'listing-link'} />
      </div>
    );
  }

  // No longer exists: an unknown id, or an erased seller. Nothing about whose it was.
  if (card.state === 'gone') {
    return (
      <div
        data-listing-card={id}
        style={{
          width: size.width, maxWidth: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px dashed var(--border-default)',
          borderRadius: 'var(--radius-md)', color: 'var(--text-muted)',
        }}
      >
        <Icon name="link-2-off" size={18} />
        <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-body)' }}>{t("This listing no longer exists")}</span>
      </div>
    );
  }

  const available = card.state === 'available';
  const label = available ? `${gameLabel(card.game)} · ${t(LISTING_MODE_NAMES[card.mode])}` : gameLabel(card.game);
  const km = card.distance && card.distance.known
    ? (card.distance.metres == null ? null : card.distance.metres / 1000)
    : undefined;

  return (
    <a
      href={hrefFor({ name: 'listing', id })}
      onClick={(e) => { e.preventDefault(); go('listing', { id }); }}
      data-listing-card={id}
      style={{
        width: size.width, maxWidth: '100%', boxSizing: 'border-box', display: 'flex', gap: size.gap, padding: size.padding,
        background: available ? 'var(--surface-card)' : 'var(--surface-sunken)',
        border: `1px solid ${available ? 'var(--border-default)' : 'var(--border-hairline)'}`,
        borderRadius: 'var(--radius-md)', textDecoration: 'none', color: 'inherit',
      }}
    >
      <div
        role="img"
        aria-label={t("Picture of the card")}
        style={{
          width: size.picW, height: size.picH, flex: 'none', boxSizing: 'border-box', overflow: 'hidden',
          borderRadius: 'var(--radius-sm)', background: 'var(--kraft-200)', border: '1px solid var(--border-default)',
          opacity: available ? 1 : 0.55,
        }}
      >
        {card.imageUrl ? (
          <img
            src={card.imageUrl}
            srcSet={card.imageSrcset || undefined}
            sizes={`${size.picW}px`}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: size.stack, minWidth: 0, flex: 1 }}>
        <span style={{ font: 'var(--type-label)', letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', ...muted }}>{label}</span>
        <span style={{ font: 'var(--type-ui)', fontSize: size.title, fontWeight: 'var(--weight-bold)', color: available ? 'var(--text-strong)' : 'var(--text-body)' }}>{card.title}</span>
        <span style={{ font: 'var(--type-body-sm)', ...muted }}>{copyLine(card)}</span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 'auto', flexWrap: 'wrap' }}>
          {!available ? (
            <Badge tone="neutral">{t("No longer available")}</Badge>
          ) : card.mode === 'sale' && card.priceCents != null ? (
            <span style={{ font: 'var(--type-price)', fontSize: size.price, color: 'var(--text-strong)' }}>{api.formatCents(card.priceCents)}</span>
          ) : (
            <Badge tone="verified">{t("For trade")}</Badge>
          )}
          {available ? (
            card.own ? (
              <span style={{ font: 'var(--type-meta)', whiteSpace: 'nowrap', ...muted }}>{t("Your listing")}</span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--type-meta)', whiteSpace: 'nowrap', minWidth: 0, ...muted }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tradingName(card.seller)}</span>
                <DistanceTag km={km} floorLabel={t("less than 1 km")} />
              </span>
            )
          ) : null}
        </div>
      </div>
    </a>
  );
}

/**
 * The bubble's text with each listing link it shows a card for shortened in
 * place to `cardboardmarket.xyz/listing/8f2c…`, still a link to the listing.
 * Null when the links were all the message said: then there is no bubble.
 */
function textAround(text, shown, me, go) {
  const links = listingLinksIn(text).filter((l) => shown.includes(l.id));
  if (!links.length) return text;
  const rest = links.reduceRight((s, l) => s.slice(0, l.start) + s.slice(l.end), text);
  if (!rest.trim()) return null;
  const parts = [];
  let at = 0;
  links.forEach((l, i) => {
    if (l.start > at) parts.push(text.slice(at, l.start));
    parts.push(
      <a
        key={i}
        href={hrefFor({ name: 'listing', id: l.id })}
        onClick={(e) => { e.preventDefault(); go('listing', { id: l.id }); }}
        style={{ color: me ? 'var(--orange-200)' : 'var(--text-link)', textDecoration: 'underline', overflowWrap: 'anywhere' }}
      >
        {shortListingLink(l)}
      </a>,
    );
    at = l.end;
  });
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

/** One line of a conversation: its bubble, if it has words, and its cards under it on the same side. */
export function MessageLine({ message, cards, wide }) {
  const { go } = useApp();
  const shown = message.listings || [];
  const body = shown.length ? textAround(message.text, shown, message.fromYou, go) : message.text;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: message.fromYou ? 'flex-end' : 'flex-start' }}>
      {body !== null ? <div className={'cbm-bubble cbm-bubble--' + (message.fromYou ? 'me' : 'them')}>{body}</div> : null}
      {shown.map((id) => <ListingLinkCard key={id} id={id} card={cards && cards[id]} wide={wide} />)}
      <span style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>{message.at}</span>
    </div>
  );
}

/**
 * Above the composer while the draft holds a listing link: what will be sent
 * with it, and an × to send the link alone. One fetch for the one listing
 * being previewed, not one per card in the thread.
 */
export function ComposerListingPreview({ listingId, onDismiss }) {
  const { t } = useApp();
  const [listing, setListing] = React.useState({ id: null, view: undefined });
  React.useEffect(() => {
    let live = true;
    setListing({ id: listingId, view: undefined });
    api.listing(listingId)
      .then((r) => { if (live) setListing({ id: listingId, view: r.listing }); })
      .catch(() => { if (live) setListing({ id: listingId, view: null }); });
    return () => { live = false; };
  }, [listingId]);

  const view = listing.id === listingId ? listing.view : undefined;
  const summary = view === undefined
    ? ' '
    : view === null
      ? t("This listing no longer exists")
      // What the card will say once sent: a listing that is not up has no price.
      : view.status !== 'active'
        ? `${view.title} · ${t("No longer available")}`
        : view.mode === 'sale' && view.priceCents != null
        ? `${view.title} · ${api.formatCents(view.priceCents)}`
        : `${view.title} · ${t("For trade")}`;

  return (
    <div
      data-composer-preview={listingId}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 6px 6px',
        border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)', background: 'var(--bg-page)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 26, height: 36, flex: 'none', boxSizing: 'border-box', overflow: 'hidden', borderRadius: 3,
          background: 'var(--kraft-200)', border: '1px solid var(--border-default)',
        }}
      >
        {view && view.imageUrl ? (
          <img src={view.imageUrl} srcSet={view.imageSrcset || undefined} sizes="26px" alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
        <span style={{ font: 'var(--type-ui)', fontWeight: 'var(--weight-bold)', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</span>
        <span style={{ font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{t("Will be sent with the listing card")}</span>
      </div>
      <IconButton icon="x" label={t("Send only the link, without the card")} variant="ghost" size="sm" onClick={onDismiss} />
    </div>
  );
}
